import { parseCurrency } from '@/lib/utils'

// Order lines show one row per unit so florists can scan small quantities, but a line
// collapses into a single "Qty N" row once it reaches 5 units, and every line collapses
// once the whole order (items and add-ons) reaches 10 units.
export const SINGLE_ROW_LINE_QUANTITY = 5
export const SINGLE_ROW_ORDER_QUANTITY = 10

export type OrderProductLine = {
  quantity?: number | string | null
  // Unit price; returnedQuantity is the refunded amount divided by it.
  amount?: number | string | null
  returnedQuantity?: number | null
}

export type OrderProductLineGroup<TLine extends OrderProductLine> = {
  key: string
  rootItems: TLine[]
  childItems: TLine[]
}

export type OrderProductRow<TLine extends OrderProductLine> = {
  key: string
  item: TLine
  relationship: 'root' | 'child'
  quantity: number
  // Units on the whole order line and where this row starts in it, so a row covering part
  // of the line can price its share.
  lineQuantity: number
  lineUnitOffset: number
  isReturned: boolean
}

// Imported lines can lack a quantity; show them as one unit. An explicit zero shows nothing.
function getUnitCount(line: OrderProductLine): number {
  if (line.quantity === null || line.quantity === undefined || line.quantity === '') {
    return 1
  }

  const quantity = Math.ceil(Number(line.quantity))

  return Number.isFinite(quantity) ? Math.max(0, quantity) : 1
}

// A unit only counts as returned once the refund covers it to within half a cent, so $9.99 of
// a $10.00 unit stays active while float noise on a full refund still counts it.
function getReturnedUnitCount(line: OrderProductLine, unitCount: number): number {
  const returnedQuantity = Number(line.returnedQuantity ?? 0)
  const unitAmount = Math.abs(parseCurrency(line.amount))
  const tolerance = unitAmount > 0 ? 0.005 / unitAmount : 1e-9

  return Math.min(unitCount, Math.max(0, Math.floor(returnedQuantity + tolerance)))
}

function getLineRows<TLine extends OrderProductLine>(
  line: TLine,
  lineKey: string,
  relationship: OrderProductRow<TLine>['relationship'],
  showSingleUnits: boolean
): OrderProductRow<TLine>[] {
  const unitCount = getUnitCount(line)
  const returnedUnitCount = getReturnedUnitCount(line, unitCount)
  const activeUnitCount = unitCount - returnedUnitCount

  if (showSingleUnits) {
    return Array.from({ length: unitCount }, (_, index) => ({
      key: `${lineKey}:unit-${index + 1}`,
      item: line,
      relationship,
      quantity: 1,
      lineQuantity: unitCount,
      lineUnitOffset: index,
      isReturned: index >= activeUnitCount,
    }))
  }

  const rows: OrderProductRow<TLine>[] = []

  if (activeUnitCount > 0) {
    rows.push({
      key: lineKey,
      item: line,
      relationship,
      quantity: activeUnitCount,
      lineQuantity: unitCount,
      lineUnitOffset: 0,
      isReturned: false,
    })
  }

  if (returnedUnitCount > 0) {
    rows.push({
      key: `${lineKey}:returned`,
      item: line,
      relationship,
      quantity: returnedUnitCount,
      lineQuantity: unitCount,
      lineUnitOffset: activeUnitCount,
      isReturned: true,
    })
  }

  return rows
}

export function getOrderProductRows<TLine extends OrderProductLine>(
  groups: OrderProductLineGroup<TLine>[]
): OrderProductRow<TLine>[] {
  const orderUnitCount = groups
    .flatMap((group) => [...group.rootItems, ...group.childItems])
    .reduce((total, line) => total + getUnitCount(line), 0)
  const collapseOrder = orderUnitCount >= SINGLE_ROW_ORDER_QUANTITY
  const showSingleUnits = (line: TLine) =>
    !collapseOrder && getUnitCount(line) < SINGLE_ROW_LINE_QUANTITY

  const groupRows = groups.map((group) => {
    const rootRows = group.rootItems.flatMap((line, index) =>
      getLineRows(line, `${group.key}:root-${index}`, 'root', showSingleUnits(line))
    )
    // With no parent row to hang from, add-ons stand on their own.
    const childRelationship = rootRows.length > 0 ? 'child' : 'root'
    const childRows = group.childItems.flatMap((line, index) =>
      getLineRows(line, `${group.key}:child-${index}`, childRelationship, showSingleUnits(line))
    )

    return { rootRows, childRows }
  })

  // Fully returned items (including their add-ons) sink below active ones.
  const isFullyReturned = ({ rootRows, childRows }: (typeof groupRows)[number]) => {
    const rows = [...rootRows, ...childRows]
    return rows.length > 0 && rows.every((row) => row.isReturned)
  }

  return [
    ...groupRows.filter((rows) => !isFullyReturned(rows)),
    ...groupRows.filter(isFullyReturned),
  ].flatMap(({ rootRows, childRows }) => [...rootRows, ...childRows])
}
